import { forwardRef, memo } from 'react';
import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useRenderColumns } from '../../columns';
import { GAP, MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../../constants';
import { SnapCarousel } from '../../main';
import { SnapCarouselWrapper } from '../components';
import { useLayout } from '../layout-context';

export const LayoutCarousel = memo(
  forwardRef<HTMLDivElement, unknown>((_, ref) => {
    const {
      breakpoints: { overflows, isMediumBreakpoint, isSmallBreakpoint }
    } = useLayout();

    const columns = useRenderColumns();

    if (overflows) {
      return (
        <SnapCarouselWrapper ref={ref} component="layout-carousel">
          <StyledSnapCarousel
            $isSmallBreakpoint={isSmallBreakpoint}
            $isMediumBreakpoint={isMediumBreakpoint}
          >
            {columns}
          </StyledSnapCarousel>
        </SnapCarouselWrapper>
      );
    }

    return (
      <Grid ref={ref} $columnsCount={columns.length}>
        {columns}
      </Grid>
    );
  })
);

const StyledSnapCarousel = styled(SnapCarousel).attrs<{
  $isSmallBreakpoint: boolean;
  $isMediumBreakpoint: boolean;
}>({
  arrowControlMargin: -2,
  columnsGap: GAP,
  withShadow: true,
  className: createReadableClass({
    component: 'layout-carousel',
    element: 'snap-carousel'
  })
})`
  display: grid;
  grid-auto-flow: column;
  padding: 0 1px;
  grid-auto-columns: ${({ $isSmallBreakpoint, $isMediumBreakpoint }) => {
    if ($isSmallBreakpoint) {
      return '90%';
    }

    if ($isMediumBreakpoint) {
      return '40%';
    }

    return `calc((100% - 2 * ${GAP}px) / 3)`;
  }};

  & > div {
    height: 100%;
    scroll-margin: 1px;
  }
`;

const Grid = styled.div.attrs<{
  $columnsCount: number;
}>({
  className: createReadableClass(
    {
      component: 'layout-carousel',
      element: 'grid'
    },
    ['es-layout-component']
  )
})`
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(
    ${({ $columnsCount }) => $columnsCount},
    minmax(${MIN_COLUMN_WIDTH}px, ${MAX_COLUMN_WIDTH}px)
  );
  gap: ${GAP}px;
`;
